import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Stack
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { validateCPFOrCNPJ, formatCPFOrCNPJ } from '../utils/cpfCnpjValidator';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import GavelIcon from '@mui/icons-material/Gavel';

interface SocialRegistrationModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: (data: CompleteProfileData) => Promise<void>;
  userInfo: {
    email: string;
    firstName: string;
    lastName: string;
    provider: 'google' | 'facebook';
  };
  loading?: boolean;
  error?: string | null;
}

interface CompleteProfileData {
  cpf_cnpj?: string;
  numero_oab?: string;
  estado_oab?: string;
  phone?: string;
}

const validationSchema = yup.object({
  cpf_cnpj: yup
    .string()
    .test('cpf-cnpj-valid', 'CPF ou CNPJ inválido', function(value) {
      return !value || validateCPFOrCNPJ(value);
    }),
  numero_oab: yup
    .string()
    .matches(/^\d+$/, 'Número da OAB deve conter apenas números'),
  estado_oab: yup
    .string()
    .when('numero_oab', {
      is: (val: string) => val && val.length > 0,
      then: (schema) => schema.required('Estado da OAB é obrigatório quando número é fornecido'),
      otherwise: (schema) => schema
    }),
  phone: yup
    .string()
    .matches(/^[\d\s\(\)\-\+]+$/, 'Formato de telefone inválido')
});

const estadosOAB = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 
  'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 
  'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const SocialRegistrationModal: React.FC<SocialRegistrationModalProps> = ({
  open,
  onClose,
  onComplete,
  userInfo,
  loading = false,
  error = null
}) => {
  const [step, setStep] = useState<'welcome' | 'form' | 'success'>('welcome');
  const [skipProfile, setSkipProfile] = useState(false);

  const formik = useFormik({
    initialValues: {
      cpf_cnpj: '',
      numero_oab: '',
      estado_oab: '',
      phone: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await onComplete(values);
        setStep('success');
        setTimeout(() => {
          onClose();
        }, 2000);
      } catch (error) {
        console.error('Erro ao completar perfil:', error);
      }
    }
  });

  const handleSkip = async () => {
    try {
      setSkipProfile(true);
      await onComplete({});
      setStep('success');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Erro ao pular perfil:', error);
      setSkipProfile(false);
    }
  };

  const handleContinue = () => {
    setStep('form');
  };

  const renderWelcomeStep = () => (
    <Box>
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
        <Typography variant="h5" component="div" gutterBottom>
          Bem-vindo, {userInfo.firstName}!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Conectado via {userInfo.provider === 'google' ? 'Google' : 'Facebook'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          Para oferecer a melhor experiência jurídica, gostaríamos de conhecer um pouco mais sobre você.
        </Alert>

        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GavelIcon color="primary" />
          Por que completar o perfil?
        </Typography>

        <Stack spacing={2} sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PersonIcon color="primary" />
            <Box>
              <Typography variant="body2" fontWeight="bold">
                Personalização
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Conteúdo jurídico adaptado ao seu perfil profissional
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BusinessIcon color="primary" />
            <Box>
              <Typography variant="body2" fontWeight="bold">
                Validação OAB
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Acesso a funcionalidades exclusivas para advogados
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckCircleIcon color="success" />
            <Box>
              <Typography variant="body2" fontWeight="bold">
                Segurança
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Proteção adicional e compliance com regulamentações
              </Typography>
            </Box>
          </Box>
        </Stack>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Leva menos de 2 minutos • Pode ser feito depois no perfil
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button 
          onClick={handleSkip} 
          color="inherit"
          disabled={loading || skipProfile}
        >
          {skipProfile ? <CircularProgress size={20} /> : 'Pular por enquanto'}
        </Button>
        <Button 
          onClick={handleContinue}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          Completar perfil (2 min)
        </Button>
      </DialogActions>
    </Box>
  );

  const renderFormStep = () => (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <DialogTitle>
        <Typography variant="h6">
          Complete seu perfil profissional
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Essas informações nos ajudam a personalizar sua experiência
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="CPF ou CNPJ (opcional)"
              name="cpf_cnpj"
              value={formik.values.cpf_cnpj}
              onChange={(e) => {
                const formatted = formatCPFOrCNPJ(e.target.value);
                formik.setFieldValue('cpf_cnpj', formatted);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.cpf_cnpj && Boolean(formik.errors.cpf_cnpj)}
              helperText={
                formik.touched.cpf_cnpj && formik.errors.cpf_cnpj ||
                'Para emissão de notas fiscais e validação profissional'
              }
              placeholder="000.000.000-00 ou 00.000.000/0000-00"
              inputProps={{ maxLength: 18 }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Número da OAB (opcional)"
              name="numero_oab"
              value={formik.values.numero_oab}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.numero_oab && Boolean(formik.errors.numero_oab)}
              helperText={
                formik.touched.numero_oab && formik.errors.numero_oab ||
                'Para acesso a funcionalidades exclusivas'
              }
              placeholder="123456"
            />
          </Grid>

          {formik.values.numero_oab && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Estado OAB"
                name="estado_oab"
                value={formik.values.estado_oab}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.estado_oab && Boolean(formik.errors.estado_oab)}
                helperText={formik.touched.estado_oab && formik.errors.estado_oab || 'Obrigatório quando número OAB é fornecido'}
                SelectProps={{
                  native: true,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              >
                <option value=""></option>
                {estadosOAB.map((estado) => (
                  <option key={estado} value={estado}>
                    {estado}
                  </option>
                ))}
              </TextField>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Telefone (opcional)"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={
                formik.touched.phone && formik.errors.phone ||
                'Para contato e recuperação de conta'
              }
              placeholder="(11) 99999-9999"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }}>
          <Chip label="Informações já preenchidas" size="small" />
        </Divider>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              value={userInfo.email}
              disabled
              InputProps={{
                endAdornment: <CheckCircleIcon color="success" />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nome"
              value={`${userInfo.firstName} ${userInfo.lastName}`}
              disabled
              InputProps={{
                endAdornment: <CheckCircleIcon color="success" />
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button 
          onClick={handleSkip}
          color="inherit"
          disabled={loading}
        >
          Pular
        </Button>
        <Button 
          type="submit"
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Finalizar cadastro'}
        </Button>
      </DialogActions>
    </Box>
  );

  const renderSuccessStep = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        Perfil criado com sucesso!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Redirecionando para o dashboard...
      </Typography>
      <CircularProgress />
    </Box>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      {step === 'welcome' && renderWelcomeStep()}
      {step === 'form' && renderFormStep()}
      {step === 'success' && renderSuccessStep()}
    </Dialog>
  );
};

export default SocialRegistrationModal;
